import { section } from '@/glow';
import { add, contains, listen, qsa, remove } from '@fluorescent/dom';
import { AnimateShoppable } from '@/lib/animation';

const selectors = {
  hotspotWrappers: '.shoppable-item',
  hotspots: '.shoppable-item__hotspot',
  productCard: '.shoppable-item__product-card',
  closeButtons: '[data-shoppable-item-close]',
};

const classes = {
  animating: 'shoppable-item--animating',
  unset: 'shoppable-item--position-unset',
  hidden: 'hidden',
  active: 'active'
};

section('shoppable', {
  onLoad() {
    this.productCards = qsa(selectors.productCard, this.container);
    this.hotspotContainers = qsa(selectors.hotspotWrappers, this.container);
    this.hotspots = qsa(selectors.hotspots, this.container);
    const closeButtons = qsa(selectors.closeButtons, this.container);

    // Self terminating mouseenter events
    this.hotspotEvents = this.hotspots.map(hotspot => {
      return {
        element: hotspot,
        event: listen(hotspot, 'mouseenter', e => {
          remove(e.currentTarget.parentNode, classes.animating);
          this.hotspotEvents.find(o => o.element === hotspot).event();
        }),
      };
    });

    this.events = [
      listen(this.hotspots, 'click', e => this._hotspotClickHandler(e)),
      listen(document, 'click', e => this._clickOutsideHandler(e)),
      listen(closeButtons, 'click', () => this._closeAll()),
      listen(this.container, 'keydown', ({ keyCode }) => {
        if (keyCode === 27) this._closeAll();
      }),
    ];

    this.AnimateShoppable = AnimateShoppable(this.container);
  },

  _hotspotClickHandler(e) {
    const hotspot = e.currentTarget;
    const wrapper = e.currentTarget.parentNode;
    const card = e.currentTarget.nextElementSibling;
    if (!card) return;

    if (contains(card, 'hidden')) {
      const cardHeight = card.offsetHeight;
      const cardWidth = card.offsetWidth;
      this._closeAll();
      card.setAttribute('aria-hidden', false);
      card.style.setProperty('--card-height', cardHeight + 'px');
      card.style.setProperty('--card-width', cardWidth + 'px');
      remove(card, classes.hidden);
      add(wrapper, classes.active);
      remove(wrapper, classes.unset);
      add(this.container, classes.flyupActive);

      // Offset flyup height and scroll hotspot into view
      if (!window.matchMedia('(min-width: 45em)').matches) {
        const hotspotOffsetMargin = 70;
        const hotspotOffsetTop = hotspot.getBoundingClientRect().top;
        const positionFromBottom =
          window.innerHeight - (hotspotOffsetTop + hotspotOffsetMargin);

        if (cardHeight > positionFromBottom) {
          const y = window.pageYOffset + cardHeight - positionFromBottom;
          window.scrollTo({
            top: y,
            behavior: 'smooth',
          });
        }
      }
    } else {
      card.setAttribute('aria-hidden', true);
      add(card, classes.hidden);
      remove(this.container, classes.flyupActive);
      remove(wrapper, classes.active);
    }
  },

  _clickOutsideHandler(e) {
    if (
      !e.target.closest(selectors.productCard) &&
      !contains(e.target, 'shoppable-item__hotspot')
    ) {
      this._closeAll();
    }
  },

  _closeAll() {
    this.productCards.forEach(card => {
      add(card, classes.hidden);
      card.setAttribute('aria-hidden', true);
    });
    this.hotspotContainers.forEach(spot => remove(spot, classes.active));
    remove(this.container, classes.flyupActive);
  },

  onUnload() {
    this.events.forEach(unsubscribe => unsubscribe());
    this.AnimateShoppable?.destroy();
  },
});
